<template>
  <Teleport to="body">
    <Transition name="ird-fade">
      <div v-if="visible" class="ird-backdrop" @keydown.esc="confirmar">
        <div
          class="ird-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ird-title"
          aria-describedby="ird-message"
        >
          <div class="ird-icon"><i class="bi bi-clipboard2-pulse"></i></div>
          <span class="ird-badge">Recordatorio semanal</span>
          <h3 id="ird-title" class="ird-title">No olvides completar el formulario de incidentes</h3>
          <p id="ird-message" class="ird-message">
            Si tuviste algún incidente esta semana, registralo en el formulario.
            Te lleva un par de minutos.
          </p>
          <div class="ird-actions">
            <a
              ref="linkRef"
              class="ird-btn ird-btn-primary"
              :href="FORM_URL"
              target="_blank"
              rel="noopener noreferrer"
              @click="confirmar"
            >
              <i class="bi bi-box-arrow-up-right"></i>
              Completar formulario
            </a>
            <button class="ird-btn ird-btn-outline" @click="confirmar">
              Okey, entendido
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
// Recordatorio del formulario de incidentes: aparece una vez por semana
// (la primera vez que el usuario entra a la app en cada semana Lun-Dom).
// Se marca como visto al tocar cualquiera de los dos botones o Esc.
//
// Se guarda por usuario y por navegador en localStorage (no en la base): si
// alguien usa dos dispositivos lo ve una vez en cada uno, que para un
// recordatorio está bien y evita tener que agregar tabla + endpoint.
import { ref, watch, nextTick } from 'vue'
import { useAuthStore } from '../../stores/auth.js'
import { weekRange } from '../../utils/dateHelpers.js'

const FORM_URL =
  'https://forms.cloud.microsoft/pages/responsepage.aspx?id=rplnyGBD5U2O1vtNc5AB69uKsAZASyBFkAwIz0etuC9URVZSU1gwRUlKT0MwNVFRMTlTMTRPUDFKWiQlQCN0PWcu&route=shorturl'
const STORAGE_PREFIX = 'srk-recordatorio-incidentes:'

const authStore = useAuthStore()
const visible = ref(false)
const linkRef = ref(null)

function semanaActual() {
  return weekRange(new Date()).start // "YYYY-MM-DD" del lunes
}

function leerUltimaSemanaVista(userId) {
  try {
    return localStorage.getItem(STORAGE_PREFIX + userId)
  } catch {
    return null
  }
}

function guardarSemanaVista(userId) {
  try {
    localStorage.setItem(STORAGE_PREFIX + userId, semanaActual())
  } catch {
    // Sin storage (modo privado, bloqueado): se volverá a mostrar, no rompe nada.
  }
}

// Se evalúa cuando hay un usuario logueado (al entrar con sesión ya iniciada o
// justo después del login, que es cuando authStore.userId pasa a tener valor).
watch(
  () => authStore.userId,
  async (userId) => {
    if (!userId) {
      visible.value = false
      return
    }
    if (leerUltimaSemanaVista(userId) === semanaActual()) return
    visible.value = true
    await nextTick()
    linkRef.value?.focus()
  },
  { immediate: true }
)

function confirmar() {
  if (authStore.userId) guardarSemanaVista(authStore.userId)
  visible.value = false
}
</script>

<style scoped>
.ird-backdrop {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(31, 41, 55, 0.55);
}
.ird-card {
  width: 100%;
  max-width: 440px;
  padding: 28px 28px 22px;
  background: var(--srk-surface, #fff);
  border-radius: 16px;
  border-top: 4px solid var(--srk-primary, #f37226);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  text-align: center;
}
.ird-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 12px;
  font-size: 1.6rem;
  color: var(--srk-primary, #f37226);
  background: var(--srk-primary-lighter, #fff4ed);
  border: 1px solid var(--srk-primary-light, #fde2d2);
  border-radius: 50%;
}
.ird-badge {
  display: block;
  margin-bottom: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--srk-primary, #f37226);
}
.ird-title {
  margin: 0 0 8px;
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.3;
  color: var(--srk-text-primary, #1f2937);
}
.ird-message {
  margin: 0 0 22px;
  font-size: var(--font-size-sm, 0.9rem);
  line-height: 1.45;
  color: var(--srk-text-secondary, #6b7280);
}
.ird-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ird-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  padding: 11px 16px;
  font-family: var(--font-family);
  font-size: var(--font-size-sm, 0.9rem);
  font-weight: 600;
  text-decoration: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.ird-btn:focus-visible {
  outline: 3px solid var(--srk-primary-light, #fde2d2);
  outline-offset: 2px;
}
.ird-btn-primary {
  color: #fff;
  background: var(--srk-primary, #f37226);
  border: 1px solid var(--srk-primary, #f37226);
}
.ird-btn-primary:hover {
  background: var(--srk-primary-hover, #e16112);
  border-color: var(--srk-primary-hover, #e16112);
}
.ird-btn-outline {
  color: var(--srk-text-primary, #1f2937);
  background: transparent;
  border: 1px solid var(--srk-border, #e5e7eb);
}
.ird-btn-outline:hover {
  background: var(--srk-bg, #f8f9fa);
  border-color: var(--srk-text-muted, #9ca3af);
}

.ird-fade-enter-active,
.ird-fade-leave-active {
  transition: opacity 0.18s ease;
}
.ird-fade-enter-from,
.ird-fade-leave-to {
  opacity: 0;
}
.ird-fade-enter-active .ird-card,
.ird-fade-leave-active .ird-card {
  transition: transform 0.18s ease;
}
.ird-fade-enter-from .ird-card,
.ird-fade-leave-to .ird-card {
  transform: translateY(8px) scale(0.98);
}
</style>
